import { Filters, FiltersWithoutSearch } from '@common/types';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import {
  MultiSelectionFilters,
  SingleSelectionFilters,
} from '@components/Templates/TemplateGallery/FilterTags/useFilterTags';
import _ from 'lodash';
import { useMemo } from 'react';

// Determines filter order on menus
export const ORDERED_FILTER_TYPES: Array<keyof FiltersWithoutSearch> = [
  'siteType',
  'industries',
  'features',
  'languages',
  'price',
  'styles',
];

export const useTemplateGalleryFilters = () => {
  const {
    state: { filters },
  } = useTemplatesContext();

  let orderedActiveFilters = ORDERED_FILTER_TYPES;

  const filterCountsMap = useMemo(() => getFilterCounts(filters), [filters]);

  const totalFilterCount = useMemo(() => {
    return Object.values(filterCountsMap).reduce(
      (accum, current) => accum + current,
      0,
    );
  }, [filterCountsMap]);

  const activeFilterMap = useMemo(
    () => getActiveFilterMap(filterCountsMap),
    [filterCountsMap],
  );

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(activeFilterMap),
    [activeFilterMap],
  );

  const hasSearchTerm = Boolean(filters.search);

  return {
    activeFilterMap,
    hasActiveFilters,
    hasSearchTerm,
    filterCountsMap,
    totalFilterCount,
    orderedFilterTypes: orderedActiveFilters,
  };
};

export const getMultiSelectionFilterCount = (
  filterSelection: Filters[MultiSelectionFilters],
) => {
  return filterSelection?.length ?? 0;
};

export const getSingleSelectionFilterCount = (
  filterSelection: Filters[SingleSelectionFilters] | undefined,
) => {
  return Number(filterSelection && filterSelection !== 'ALL');
};

const getFilterCounts = (filters: Filters) => {
  const { price, styles, features, industries, siteType, languages } = filters;

  const filterCounts: Record<keyof FiltersWithoutSearch, number> = {
    price: getSingleSelectionFilterCount(price),
    siteType: getSingleSelectionFilterCount(siteType),
    styles: getMultiSelectionFilterCount(styles),
    features: getMultiSelectionFilterCount(features),
    industries: getMultiSelectionFilterCount(industries),
    languages: getMultiSelectionFilterCount(languages),
  };

  return filterCounts;
};

export const getActiveFilterMap = (
  filterCounts: Record<keyof FiltersWithoutSearch, number>,
) => {
  const activeFilterMap = {} as Record<keyof Filters, boolean>;
  for (const [key, value] of Object.entries(filterCounts)) {
    activeFilterMap[key as keyof Filters] = Boolean(value);
  }

  return activeFilterMap;
};

export const getHasActiveFilters = (
  filtersActive: Record<keyof Filters, boolean>,
) => {
  for (const [_filterType, isFilterEnabled] of Object.entries(filtersActive)) {
    if (isFilterEnabled) {
      return true;
    }
  }

  return false;
};
